import z from 'zod'
import { AnnatureForm } from './annature'
import { DocusignForm } from './docusign'
import { ReiForm, ReiFormType } from './rei'
import { InternalSaleListingSchema } from './saleListing'
import { ZohoSignForm } from './zoho'

export const EsignFormTypes = ['fundDisbursementForm'] as const
export type EsignFormType = (typeof EsignFormTypes)[number]

export const ReiAndEsignFormTypes = ['contractForm', 'sellerDisclosureForm'] as const
export type ReiAndEsignFormType = (typeof ReiAndEsignFormTypes)[number]

export type SigningService = 'docusign' | 'zoho' | 'annature'

export type AnnatureOrManualForm = AnnatureForm | ManualForm

export type EsignForm = DocusignForm | ZohoSignForm | AnnatureForm | ManualForm

export interface EsignFormStructure {
    esign?: AnnatureOrManualForm
    signingService: SigningService
}

export interface ReiAndEsignFormStructure extends EsignFormStructure {
    rei: ReiForm
}

export interface ManualForm {
    formStatus: 'manual upload'
    fileUrl: string
    fileName: string
}

export interface FormToListingTable {
    orgId: string
    formType: ReiFormType | ReiAndEsignFormType | EsignFormType | 'generic'
    formId: string
    listingId: string
    service: 'docusign' | 'zoho' | 'rei' | 'annature'
    createdAt: string
    timeToDie: number
}

export const getFormName = (formType: ReiFormType | EsignFormType | ReiAndEsignFormType) => {
    switch (formType) {
        case 'agentAppointmentForm':
            return 'Agent Appointment Form'
        case 'tenantConsentOpenHomeForm':
            return 'Tenant Consent Open Home Form'
        case 'intentToSellForm':
            return 'Intent to Sell Form'
        case 'entryNoticeForm':
            return 'Entry Notice Form'
        case 'contractForm':
            return 'Contract'
        case 'sellerDisclosureForm':
            return 'Seller Disclosure Statement'
        case 'fundDisbursementForm':
            return 'Fund Disbursement Form'
        default:
            return 'Unknown Form'
    }
}

// Generic Esign Document Schema
export interface GenericEsignDocument {
    formName: string
    form: AnnatureForm
}

export const getEsignFormFromListing = (
    params: CoreFormTypeSchema & { listing: InternalSaleListingSchema },
): AnnatureOrManualForm | undefined => {
    const { listing, formType, ...data } = params

    if (formType === 'generic') {
        return listing.genericEsignDocuments?.[(data as { formId: string }).formId]?.form
    } else {
        return listing[formType]?.esign as AnnatureOrManualForm | undefined
    }
}

export const isReiAndEsignFormType = (
    formType: ReiFormType | EsignFormType | ReiAndEsignFormType | 'generic',
): formType is ReiAndEsignFormType => {
    return ReiAndEsignFormTypes.includes(formType as ReiAndEsignFormType)
}

export const getReiFormFromListing = (
    listing: InternalSaleListingSchema,
    formType: ReiFormType | ReiAndEsignFormType,
): ReiForm | undefined => {
    if (isReiAndEsignFormType(formType)) {
        return listing[formType]?.rei
    }
    return listing[formType] as ReiForm | undefined
}

export const getFormFromListing = (
    listing: InternalSaleListingSchema,
    formType: ReiFormType | EsignFormType | ReiAndEsignFormType,
): ReiForm | AnnatureOrManualForm | undefined => {
    if (formType === 'contractForm' || formType === 'sellerDisclosureForm') {
        // For contractForm, prioritize esign if defined, otherwise rei
        return listing.contractForm?.esign || listing.contractForm?.rei
    }
    if (formType === 'fundDisbursementForm') {
        // For fundDisbursementForm, return the esign form
        return listing.fundDisbursementForm?.esign
    }
    // For other form types, return the direct property
    return listing[formType]
}

export const CoreFormTypeSchema = z
    .object({
        formType: z.literal('generic'),
        formId: z.string(),
    })
    .or(
        z.object({
            formType: z.enum([...EsignFormTypes, ...ReiAndEsignFormTypes]),
        }),
    )

export type CoreFormTypeSchema = z.infer<typeof CoreFormTypeSchema>
